import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { McpOAuthGrant } from '@/api/v4/mcp-oauth.api';
import { timeAgo } from '@/features/notifications/utils/time-ago';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';

import type { AccountScopedMcpConnectorConfig } from '../mcp-oauth/mcp-oauth-connector-config';
import { resolveMcpOAuthLastErrorMessage } from '../mcp-oauth/mcp-oauth-errors';
import { McpOAuthSetupTabs } from '../mcp-oauth/mcp-oauth-setup-tabs';
import { SectionDivider } from '../mcp-oauth/mcp-oauth.styles';
import {
  DangerTextButton,
  ErrorCard,
  SetupStepsAnchor,
} from './claude-mcp-connector.styles';
import { ClaudeMcpConnectorHeader } from './claude-mcp-connector-header';

interface ClaudeMcpConnectorErrorViewProps {
  connectorConfig: AccountScopedMcpConnectorConfig;
  copiedFieldId: string | null;
  grant: McpOAuthGrant;
  isConnecting: boolean;
  onCopy: (value: string, fieldId: string) => void;
  onDisconnect: (grant: McpOAuthGrant) => void;
  onReconnect: () => void;
  setupStepsRef: RefObject<HTMLDivElement | null>;
  showAdvancedSettingsHint: boolean;
}

export const ClaudeMcpConnectorErrorView = ({
  connectorConfig,
  copiedFieldId,
  grant,
  isConnecting,
  onCopy,
  onDisconnect,
  onReconnect,
  setupStepsRef,
  showAdvancedSettingsHint,
}: ClaudeMcpConnectorErrorViewProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const errorDescription = resolveMcpOAuthLastErrorMessage(grant.lastError, t);

  return (
    <ErrorCard>
      <Stack fullWidth gap="14px">
        <ClaudeMcpConnectorHeader
          badge={<ErrorBadge>{t`mcpOAuth.statusError`}</ErrorBadge>}
          subtitle={t`mcpOAuth.cardSubtitleConnectViaMcp`}
          title={t`mcpOAuth.cardTitle`}
        />

        <ErrorBanner fullWidth gap="0" role="alert">
          <ErrorBannerTitle>
            {t`mcpOAuth.errorTokenRefreshFailedTitle`}
          </ErrorBannerTitle>
          <ErrorBannerDescription>{errorDescription}</ErrorBannerDescription>
        </ErrorBanner>

        {grant.lastUsedAt ? (
          <ErrorLastUsedRow
            alignItems="center"
            fullWidth
            justify="space-between"
          >
            <span>{t`mcpOAuth.connectedDetailsLastUsed`}</span>
            <span>{timeAgo(grant.lastUsedAt)}</span>
          </ErrorLastUsedRow>
        ) : null}

        <SetupStepsAnchor defaultRef={setupStepsRef} fullWidth>
          <McpOAuthSetupTabs
            connectorConfig={connectorConfig}
            copiedFieldId={copiedFieldId}
            onCopy={onCopy}
            showAdvancedSettingsHint={showAdvancedSettingsHint}
          />
        </SetupStepsAnchor>

        <SectionDivider />
        <ErrorActions alignItems="center" fullWidth gap="12px">
          <Button
            disabled={isConnecting}
            onClick={onReconnect}
            type="button"
            variant="primary"
          >
            {isConnecting ? t`mcpOAuth.connecting` : t`mcpOAuth.reconnect`}
          </Button>
          <DangerTextButton onClick={() => onDisconnect(grant)} type="button">
            {t`mcpOAuth.disconnect`}
          </DangerTextButton>
        </ErrorActions>
      </Stack>
    </ErrorCard>
  );
};

const ErrorBadge = styled('span')`
  background-color: ${({ theme }) => theme.colors.spotlight.stage.deal.bg};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.spotlight.stage.deal.text};
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 9px;
`;

const ErrorBanner = styled(Stack)`
  background-color: ${({ theme }) => theme.colors.spotlight.stage.deal.bg};
  border-radius: 8px;
  padding: 12px 14px;
`;

const ErrorBannerTitle = styled('p')`
  color: ${({ theme }) => theme.colors.spotlight.stage.deal.text};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 4px;
`;

const ErrorBannerDescription = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 12.5px;
  line-height: 1.5;
  margin: 0;
`;

const ErrorLastUsedRow = styled(Flexbox)`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  padding-bottom: 12px;

  & > span:first-child {
    color: ${({ theme }) => theme.colors.gray.c10};
  }
`;

const ErrorActions = styled(Flexbox)`
  flex-wrap: wrap;
`;
